/**
 * VizFrameBlock - Container for visualizations
 * 
 * Supports multiple renderer types: chart, map, timeline, network, etc.
 * Heavy renderers are lazy loaded.
 * 
 * IMPORTANT: Supports both legacy CIG format and new SIB format:
 * - Legacy: data is array of {value, name} objects
 * - SIB: data has `series` array with `{x, y}` data points
 */

import React, { Suspense, lazy } from 'react'
import { VizFrameBlock as VizFrameBlockType } from '../../../../lib/cockpit/schemas'
import { useScenario } from '../../scenario/ScenarioContext'
import SIBChartRenderer from '../../renderers/SIBChartRenderer'

// Lazy load chart renderers
const ChartRenderer = lazy(() => import('../../renderers/ChartRenderer'))
const TableRenderer = lazy(() => import('../../renderers/TableRenderer'))
const MapRenderer = lazy(() => import('../../renderers/MapRenderer'))

interface VizFrameBlockProps {
  data: VizFrameBlockType['data']
  blockId: string
  compact?: boolean
}

function VizSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded-lg" />
    </div>
  )
}

/**
 * Detect if data is in SIB format (has series with x/y data points)
 */
function isSIBChartData(data: any): boolean {
  if (!data || typeof data !== 'object') return false
  
  // SIB format has `series` array or `type` with series
  if (Array.isArray(data.series) && data.series.length > 0) {
    const firstSeries = data.series[0]
    if (Array.isArray(firstSeries.data) && firstSeries.data.length > 0) {
      const firstPoint = firstSeries.data[0]
      // SIB data points have x and y
      return 'x' in firstPoint && 'y' in firstPoint
    }
  }
  
  return false
}

function VizFrameBlock({ data, compact }: VizFrameBlockProps) {
  const { isTransitioning, getScenarioValue } = useScenario()

  // Get scenario-aware data if applicable
  const chartData = data.scenario_data_key && data.inline_data
    ? getScenarioValue(
        (data.inline_data as any)?.[data.scenario_data_key],
        data.inline_data
      )
    : data.inline_data

  // Check if this is SIB format data
  const useSIBRenderer = isSIBChartData(chartData)

  const renderContent = () => {
    // Handle SIB chart types (line, bar, pie, area, scatter, etc.)
    const renderer = data.renderer?.toLowerCase() || ''
    const isSIBChartType = ['line', 'bar', 'pie', 'area', 'scatter', 'gauge', 'heatmap', 
                           'radar', 'funnel', 'waterfall', 'candlestick', 'treemap', 
                           'sunburst', 'sankey', 'histogram', 'boxplot', 'violin', 'bullet'].includes(renderer)
    
    // Use SIB renderer for SIB format data OR SIB chart types
    if (useSIBRenderer || isSIBChartType) {
      const sibData = chartData as any
      return (
        <SIBChartRenderer
          data={{
            type: renderer,
            series: sibData?.series || [],
            interactive: sibData?.interactive,
            x_axis_label: sibData?.x_axis_label,
            y_axis_label: sibData?.y_axis_label,
            x_axis_unit: sibData?.x_axis_unit,
            y_axis_unit: sibData?.y_axis_unit,
            title: data.caption || sibData?.title, // Pass title for format detection
          }}
          compact={compact}
        />
      )
    }
    
    switch (data.renderer) {
      case 'line_chart':
      case 'bar_chart':
      case 'area_chart':
      case 'scatter_chart':
      case 'pie_chart':
        return (
          <Suspense fallback={<VizSkeleton />}>
            <ChartRenderer
              type={data.renderer}
              data={chartData}
              options={data.render_options}
              compact={compact}
            />
          </Suspense>
        )
      
      case 'table':
        return (
          <Suspense fallback={<VizSkeleton />}>
            <TableRenderer
              data={chartData}
              options={data.render_options}
              compact={compact}
            />
          </Suspense>
        )
      
      case 'map':
        return (
          <Suspense fallback={<VizSkeleton />}>
            <MapRenderer
              data={{
                map_id: (chartData as any)?.map_id || 'map-1',
                map_type: (chartData as any)?.map_type || 'point',
                title: data.caption || (chartData as any)?.title || 'Geographic Analysis',
                subtitle: (chartData as any)?.subtitle,
                focus_location: (chartData as any)?.focus_location,
                base_map: (chartData as any)?.base_map,
                points: (chartData as any)?.points || [],
                regions: (chartData as any)?.regions || [],
                color_scale: (chartData as any)?.color_scale,
                legend_title: (chartData as any)?.legend_title,
                zoom_enabled: (chartData as any)?.zoom_enabled,
                map_style: (chartData as any)?.map_style,
              }}
              compact={compact}
            />
          </Suspense>
        )
      
      case 'timeline':
        return (
          <div className="h-64 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
            <div className="text-center text-gray-500 dark:text-gray-400">
              <svg className="w-12 h-12 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm">Timeline visualization</p>
            </div>
          </div>
        )
      
      case 'network_graph':
        return (
          <div className="h-64 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
            <div className="text-center text-gray-500 dark:text-gray-400">
              <svg className="w-12 h-12 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              <p className="text-sm">Network graph</p>
              <p className="text-xs">View in Connections tab</p>
            </div>
          </div>
        )
      
      case 'image':
        return chartData && typeof chartData === 'object' && 'url' in (chartData as any) ? (
          <img
            src={(chartData as any).url}
            alt={data.caption}
            className="max-w-full h-auto rounded-lg"
          />
        ) : (
          <div className="h-64 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
            <p className="text-gray-500">No image available</p>
          </div>
        )
      
      default:
        return (
          <div className="h-64 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center">
            <p className="text-gray-500 dark:text-gray-400">
              Visualization type: {data.renderer}
            </p>
          </div>
        )
    }
  }

  return (
    <div className={`
      transition-opacity duration-150
      ${isTransitioning ? 'opacity-50' : 'opacity-100'}
    `}>
      {/* Visualization - always ensure minimum height for ResponsiveContainer */}
      <div className={compact ? 'h-48 min-h-48' : 'min-h-[320px]'}>
        {renderContent()}
      </div>
    </div>
  )
}

export default VizFrameBlock



